import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { Button } from "../components/ui/button";
import { Icon } from "../components/ui/icons";
import { BackButton, Centered, Screen, SectionLabel, Sheet } from "../components/ui/layout";
import { Spinner } from "../components/ui/spinner";
import { formatDuration, formatMoney, formatTime } from "../libs/format";
import { getStats, type ShiftStats } from "../modules/states/api";

// «Мой день»: итоги текущей (или последней за сегодня) смены. Отсюда же закрывают смену —
// сотрудник видит, с чем уходит, а случайный тап по «Закрыть» не срабатывает без подтверждения.
export function StatsScreen({
  busy,
  error,
  now,
  shiftOpen,
  onBack,
  onClose,
}: {
  busy: boolean;
  error: string | null;
  now: number;
  shiftOpen: boolean;
  onBack: () => void;
  onClose: () => void;
}) {
  const [stats, setStats] = useState<ShiftStats | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = () => {
    setFailed(false);
    getStats()
      .then(setStats)
      .catch(() => setFailed(true));
  };
  useEffect(load, []);

  // Итоги тянем один раз, а время на смене доводим до «сейчас» сами — иначе цифра стоит на месте.
  const onShift = stats
    ? stats.onShiftSeconds + (shiftOpen && !stats.shiftClosedAt ? Math.floor((now - loadedAt(stats)) / 1000) : 0)
    : 0;

  return (
    <Screen
      error={error}
      header={<BackButton onClick={onBack}>{shiftOpen ? "В зал" : "Назад"}</BackButton>}
      footer={
        shiftOpen && (
          <Button variant="secondary" className="text-[var(--color-danger)]" onClick={() => setConfirm(true)}>
            <Icon name="power" className="size-5" />
            Закрыть смену
          </Button>
        )
      }
    >
      <h1 className="text-2xl font-semibold">Мой день</h1>
      <p className="pt-1 pb-3 text-sm text-[var(--color-muted)]">
        {new Date(now).toLocaleDateString("ru-RU", { day: "numeric", month: "long", weekday: "long" })}
      </p>

      {failed ? (
        <Centered>
          <p className="text-center text-[var(--color-muted)]">Не удалось загрузить итоги.</p>
          <Button variant="secondary" className="w-auto" onClick={load}>
            Повторить
          </Button>
        </Centered>
      ) : !stats ? (
        <Centered>
          <Spinner className="size-8 text-[var(--color-accent)]" />
        </Centered>
      ) : (
        <StatsBody stats={stats} onShift={onShift} />
      )}

      {confirm && stats && (
        <Sheet onClose={() => setConfirm(false)}>
          <h2 className="text-xl font-semibold">Закрыть смену?</h2>
          <p className="text-[var(--color-muted)]">
            На смене {formatDuration(onShift)}
            {stats.shiftOpenedAt && `, с ${formatTime(stats.shiftOpenedAt)}`}. Сделок: {stats.outcomes.deal}
            {stats.dealsAmount > 0 && ` на ${formatMoney(stats.dealsAmount)}`}. Итоги уйдут в 1С.
          </p>
          {/* Ошибку под шторкой не видно — дублируем её здесь. */}
          {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}
          <div className="flex flex-col gap-2 pt-2">
            <Button variant="danger" loading={busy} onClick={onClose}>
              Закрыть смену
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => setConfirm(false)}>
              Продолжить работу
            </Button>
          </div>
        </Sheet>
      )}
    </Screen>
  );
}

const loadedAtMap = new WeakMap<ShiftStats, number>();
function loadedAt(stats: ShiftStats) {
  if (!loadedAtMap.has(stats)) loadedAtMap.set(stats, Date.now());
  return loadedAtMap.get(stats)!;
}

function StatsBody({ stats, onShift }: { stats: ShiftStats; onShift: number }) {
  const maxSeconds = Math.max(1, ...stats.byState.map((item) => item.seconds));
  const maxReasons = Math.max(1, ...stats.refusalReasons.map((item) => item.count));

  if (!stats.shiftOpenedAt) {
    return (
      <Centered>
        <p className="max-w-72 text-center text-[var(--color-muted)]">Сегодня смен ещё не было.</p>
      </Centered>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div className="grid grid-cols-2 gap-2.5">
        <Figure label="На смене" value={formatDuration(onShift)} />
        <Figure label="Обслужено" value={String(stats.consultations)} />
        <Figure label="Сделок" value={String(stats.outcomes.deal)} hint={formatMoney(stats.dealsAmount)} accent />
        <Figure
          label="Конверсия"
          value={stats.conversion === undefined ? "—" : `${Math.round(stats.conversion * 100)}%`}
          hint={`отказов: ${stats.outcomes.refusal}`}
        />
      </div>

      {stats.byState.length > 0 && (
        <Card>
          <SectionLabel>Время по состояниям</SectionLabel>
          {stats.byState.map((item) => (
            <Bar
              key={item.stateId}
              label={item.stateName}
              value={formatDuration(item.seconds)}
              share={item.seconds / maxSeconds}
            />
          ))}
        </Card>
      )}

      {stats.refusalReasons.length > 0 && (
        <Card>
          <SectionLabel>Причины отказов</SectionLabel>
          {stats.refusalReasons.map((item) => (
            <Bar
              key={item.reasonName}
              label={item.reasonName}
              value={String(item.count)}
              share={item.count / maxReasons}
              tone="danger"
            />
          ))}
        </Card>
      )}

      {stats.deals.length > 0 && (
        <Card>
          <SectionLabel>Сделки</SectionLabel>
          {stats.deals.map((deal, index) => (
            <div
              key={index}
              className="flex items-baseline gap-3 border-t border-[var(--color-line)] py-2 first:border-0"
            >
              <span className="w-12 text-sm text-[var(--color-muted)] tabular-nums">{formatTime(deal.finishedAt)}</span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="font-medium">{deal.outcomeName}</span>
                <span className="truncate text-sm text-[var(--color-muted)]">{deal.title ?? "без документа"}</span>
              </span>
              <span className="font-semibold whitespace-nowrap tabular-nums">{formatMoney(deal.amount)}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function Card({ children }: { children: ReactNode }) {
  return <section className="rounded-2xl bg-[var(--color-surface)] px-4 pb-3">{children}</section>;
}

function Figure({ label, value, hint, accent }: { label: string; value: string; hint?: string; accent?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-2xl bg-[var(--color-surface)] px-4 py-3">
      <span className="text-sm text-[var(--color-muted)]">{label}</span>
      <span
        className={`text-2xl font-semibold tracking-tight tabular-nums ${accent ? "text-[var(--color-accent)]" : ""}`}
      >
        {value}
      </span>
      {hint && <span className="text-sm text-[var(--color-muted)] tabular-nums">{hint}</span>}
    </div>
  );
}

function Bar({
  label,
  value,
  share,
  tone = "accent",
}: {
  label: string;
  value: string;
  share: number;
  tone?: "accent" | "danger";
}) {
  return (
    <div className="flex items-center gap-3 py-1.5 text-sm">
      <span className="w-28 shrink-0 truncate">{label}</span>
      <span className="h-2 flex-1 overflow-hidden rounded-full bg-black/5">
        <span
          className="block h-full rounded-full"
          style={{
            width: `${Math.max(4, share * 100)}%`,
            background: tone === "danger" ? "var(--color-danger)" : "var(--color-accent)",
          }}
        />
      </span>
      <span className="w-20 shrink-0 text-right text-[var(--color-muted)] tabular-nums">{value}</span>
    </div>
  );
}
